import { useState, useCallback } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import { requestEntityExport } from "~/api/entity-export";
import { buildExportPayload } from "./buildExportPayload";
import { downloadBlob } from "./downloadBlob";
import { filterAllowedDatasets } from "./filterAllowedDatasets";
import type { EntityExportSelection } from "./types";

export interface UseEntityExportResult {
  /** Trigger export with current selection and Entity+ flag. */
  exportEntities: (
    selection: EntityExportSelection,
    isEntityPlusActive: boolean,
  ) => Promise<void>;
  isExporting: boolean;
  error: string | null;
}

/**
 * Hook that orchestrates entity export: filters allowed datasets, builds
 * payload, calls API, triggers download. Uses WorkOS getAccessToken for auth.
 */
export function useEntityExport(): UseEntityExportResult {
  const { getAccessToken } = useAuth();
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportEntities = useCallback(
    async (
      selection: EntityExportSelection,
      isEntityPlusActive: boolean,
    ): Promise<void> => {
      setIsExporting(true);
      setError(null);

      try {
        const allowedDatasets = filterAllowedDatasets(
          selection.datasets,
          isEntityPlusActive,
        );
        const payload = buildExportPayload({
          ...selection,
          datasets: allowedDatasets,
        });
        const token = await getAccessToken();
        const { blob, filename } = await requestEntityExport(
          payload,
          token ?? null,
        );
        downloadBlob(blob, filename);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Export failed";
        setError(message);
        throw err;
      } finally {
        setIsExporting(false);
      }
    },
    [getAccessToken],
  );

  return { exportEntities, isExporting, error };
}
